import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, MonitorSmartphone, Sparkles } from "lucide-react";

import { cn, getFileUrl } from "@/lib/utils";

import type { DiagnosticsInfo } from "../utils/diagnostics";
import type { GenerationReference } from "../utils/ticket-context";

/**
 * Mesaja iliştirilmiş teknik ortam bilgisi — varsayılan olarak kapalı,
 * konuşma akışını bölmemesi için tek satırlık bir özet gösterir.
 */
export const DiagnosticsCard: React.FC<{ diagnostics: DiagnosticsInfo }> = ({
  diagnostics,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const rows: { label: string; value?: string }[] = [
    { label: t("support.tools.diagBrowser"), value: diagnostics.browser },
    { label: t("support.tools.diagOs"), value: diagnostics.os },
    { label: t("support.tools.diagScreen"), value: diagnostics.screen },
    { label: t("support.tools.diagViewport"), value: diagnostics.viewport },
    { label: t("support.tools.diagPage"), value: diagnostics.page },
    { label: t("support.tools.diagTimezone"), value: diagnostics.timezone },
    { label: t("support.tools.diagLanguage"), value: diagnostics.language },
  ].filter((row) => !!row.value);

  return (
    <div className="w-full max-w-sm overflow-hidden rounded-xl border border-border bg-card/80">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <MonitorSmartphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate text-xs font-medium text-foreground">
          {t("support.tools.diagnostics")}
        </span>
        <span className="truncate text-[11px] text-muted-foreground">
          {diagnostics.browser} · {diagnostics.os}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t border-border px-3 py-2.5">
          {rows.map((row) => (
            <React.Fragment key={row.label}>
              <dt className="text-[11px] text-muted-foreground">{row.label}</dt>
              <dd className="truncate text-[11px] font-medium text-foreground">
                {row.value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
};

/**
 * Mesaja iliştirilmiş üretim referansı — küçük önizleme ve prompt metni.
 */
export const GenerationCard: React.FC<{
  generation: GenerationReference;
  onImageClick?: (url: string) => void;
}> = ({ generation, onImageClick }) => {
  const { t } = useTranslation();
  const url = getFileUrl(generation.fileUrl);
  const isImage = generation.type === "image" && !!url;

  return (
    <div className="flex w-full max-w-sm gap-3 overflow-hidden rounded-xl border border-border bg-card/80 p-2.5">
      <button
        type="button"
        disabled={!isImage}
        onClick={() => isImage && url && onImageClick?.(url)}
        className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-muted disabled:cursor-default"
      >
        {isImage ? (
          <img
            src={url}
            alt={generation.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[10px] uppercase text-muted-foreground">
            {generation.type}
          </span>
        )}
      </button>

      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
          <Sparkles className="h-3 w-3" />
          {t("support.tools.generation")}
        </span>
        <span className="truncate text-xs font-medium text-foreground">
          {generation.title}
        </span>
        {generation.prompt && (
          <p className="line-clamp-3 text-[11px] leading-snug text-muted-foreground">
            {generation.prompt}
          </p>
        )}
      </div>
    </div>
  );
};
